import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'motion/react';
import { Button } from '@/src/modules/components/Button';
import { Icons } from '@/src/modules/components/Icons';
import { publicGetTemplates } from '@/src/infrastructure/lib/api';
import { TemplateCardPreview } from '@/src/modules/renderer/TemplateCardPreview';
import { Footer } from '@/src/modules/components/Footer';
import type { ApiTemplate } from '@/src/types';

const OCCASION_FILTERS = [
  { value: '', label: 'All' },
  { value: 'WEDDING', label: 'Wedding' },
  { value: 'BIRTHDAY', label: 'Birthday' },
  { value: 'ANNIVERSARY', label: 'Anniversary' },
  { value: 'BABY_SHOWER', label: 'Baby Shower' },
  { value: 'HOUSEWARMING', label: 'Housewarming' },
  { value: 'CORPORATE', label: 'Corporate' },
];

export function Designs() {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState<ApiTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedOccasion, setSelectedOccasion] = useState('');
  const [showPremiumOnly, setShowPremiumOnly] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const useTemplate = (template: ApiTemplate) => {
    navigate('/wizard', {
      state: {
        occasionType: template.occasionType,
        templateId: template.id,
        initialStep: 3,
      },
    });
  };

  useEffect(() => {
    publicGetTemplates()
      .then(setTemplates)
      .catch((err) => setError(err instanceof Error ? err.message : 'Failed to load templates.'))
      .finally(() => setLoading(false));
  }, []);

  const filteredTemplates = templates.filter((t) => {
    if (selectedOccasion && t.occasionType !== selectedOccasion) return false;
    if (showPremiumOnly && !t.isPremium) return false;
    if (searchTerm && !t.name.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    return true;
  });

  return (
    <div className="flex flex-col w-full bg-surface-lowest">
      {/* Hero */}
      <section className="relative pt-20 pb-16 lg:pt-28 lg:pb-24 overflow-hidden">
        <div className="absolute top-0 right-0 -mr-[15%] -mt-[10%] w-[50%] h-[70%] rounded-full bg-primary/15 blur-[100px] pointer-events-none" />

        <div className="max-w-4xl mx-auto px-6 relative z-10 text-center">
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-5xl lg:text-6xl font-bold text-on-surface mb-6 leading-tight">
              Find Your <span className="text-primary">Perfect Design</span>
            </h1>
            <p className="text-lg text-on-surface-variant max-w-2xl mx-auto">
              Browse our collection of professionally designed invitation templates for every occasion.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Filters & Search */}
      <section className="py-12 bg-surface border-b border-outline-variant/20">
        <div className="max-w-6xl mx-auto px-6">
          {/* Search */}
          <div className="mb-8 flex items-center gap-3 bg-surface-low rounded-xl px-4 py-3 border border-outline-variant/30 focus-within:border-primary/40 transition-colors">
            <Icons.Search size={18} className="text-on-surface-variant flex-shrink-0" />
            <input
              type="text"
              placeholder="Search designs by name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="flex-1 bg-transparent outline-none text-on-surface placeholder-on-surface-variant"
            />
          </div>

          {/* Occasion Filter */}
          <div className="mb-8">
            <p className="text-sm font-semibold text-on-surface-variant mb-3">Occasion Type</p>
            <div className="flex flex-wrap gap-2">
              {OCCASION_FILTERS.map((filter) => (
                <button
                  key={filter.value}
                  onClick={() => setSelectedOccasion(filter.value)}
                  className={`px-4 py-2 rounded-lg font-medium text-sm transition-all ${
                    selectedOccasion === filter.value
                      ? 'bg-primary text-white'
                      : 'bg-surface-low text-on-surface hover:bg-primary/10'
                  }`}
                >
                  {filter.label}
                </button>
              ))}
            </div>
          </div>

          {/* Premium Filter */}
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="premium-filter"
              checked={showPremiumOnly}
              onChange={(e) => setShowPremiumOnly(e.target.checked)}
              className="w-4 h-4 rounded accent-primary"
            />
            <label htmlFor="premium-filter" className="text-sm font-medium text-on-surface">
              Show Premium designs only
            </label>
          </div>
        </div>
      </section>

      {/* Templates Grid */}
      <section className="py-16">
        <div className="max-w-6xl mx-auto px-6">
          {loading && (
            <div className="flex justify-center py-24">
              <div className="w-10 h-10 rounded-full border-4 border-primary border-t-transparent animate-spin" />
            </div>
          )}

          {!loading && error && (
            <div className="text-center py-16 text-status-failed font-medium">{error}</div>
          )}

          {!loading && !error && filteredTemplates.length === 0 && (
            <div className="text-center py-24">
              <Icons.FileText size={48} className="text-on-surface-variant/30 mx-auto mb-4" />
              <p className="text-on-surface-variant text-lg">No designs found matching your criteria.</p>
            </div>
          )}

          {!loading && !error && filteredTemplates.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-4 gap-6">
              {filteredTemplates.map((template, idx) => (
                <motion.article
                  key={template.id}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: (idx % 4) * 0.1 }}
                  viewport={{ once: true }}
                  className="group bg-surface rounded-3xl border border-outline-variant/30 overflow-hidden shadow-sm hover:shadow-xl hover:border-primary/40 transition-all flex flex-col"
                >
                  <button
                    type="button"
                    onClick={() => useTemplate(template)}
                    aria-label={`Use the ${template.name} template`}
                    className="relative aspect-[4/5] bg-surface-low overflow-hidden text-left"
                  >
                    <TemplateCardPreview template={template} />
                    <span className="absolute inset-0 bg-brand-deep/20 backdrop-blur-[2px] opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  </button>
                  <div className="p-5 flex flex-col flex-1">
                    <div className="flex items-start justify-between gap-3 mb-3">
                      <div>
                        <h2 className="font-bold text-on-surface leading-tight">{template.name}</h2>
                        <p className="text-xs text-on-surface-variant mt-1 capitalize">
                          {template.occasionType.replace(/_/g, ' ').toLowerCase()}
                        </p>
                      </div>
                      {template.isPremium && (
                        <span className="text-[10px] font-black uppercase bg-primary/10 text-primary px-2 py-1 rounded-full flex-shrink-0">
                          Premium
                        </span>
                      )}
                    </div>
                    {template.description && (
                      <p className="text-sm text-on-surface-variant line-clamp-2 mb-3">
                        {template.description}
                      </p>
                    )}
                    <Button
                      className="w-full mt-auto"
                      size="sm"
                      onClick={() => useTemplate(template)}
                    >
                      Use Design
                    </Button>
                  </div>
                </motion.article>
              ))}
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}
